/**
 * Consent for anything stored on a visitor's device.
 *
 * French law is article 82 of the loi Informatique et Libertés, which
 * transposes the ePrivacy directive. It does not say "cookies": it covers
 * *reading from or writing to* the user's terminal by any means — cookies,
 * `localStorage`, `sessionStorage`, IndexedDB, a fingerprint. Consent is
 * required for all of it **except** what is strictly necessary to deliver the
 * service the person asked for.
 *
 * What the CNIL requires of a consent mechanism, and what this implements:
 *
 *   * refusing must be as easy as accepting — one click, same level;
 *   * nothing non-essential may be read or written before a choice is made;
 *   * consent is per purpose, never a single "accept everything";
 *   * no pre-ticked boxes: the default is refusal;
 *   * the choice must be revocable at any time, as easily as it was given;
 *   * the choice itself is recorded, with what was agreed to and when.
 *
 * **The state of this application today: it stores nothing non-essential.**
 * There is no analytics, no advertising, no third-party script, no session
 * cookie — the API token lives in React state and is gone when the tab closes.
 * The only thing ever written is the consent record below, and only once
 * somebody has expressed a preference. Under article 82 that is exempt, so a
 * banner is not required and this one does not appear.
 *
 * That is exactly why the machinery exists now rather than later. `TRACKERS` is
 * empty; add one entry to it and the banner appears, refusal becomes the
 * default, and `isAllowed` starts gating it. The alternative — adding analytics
 * first and the consent flow afterwards — is how a site ships a month of
 * unlawful collection.
 */

export type ConsentCategory = 'necessary' | 'analytics' | 'support' | 'marketing';

export interface TrackerDeclaration {
  /** Stable key, used in the preferences dialog and in the cookie policy. */
  readonly id: string;
  readonly category: Exclude<ConsentCategory, 'necessary'>;
  /** What it is, in the words a salon owner would use. */
  readonly purpose: string;
  /** Who receives the data. Named, because "our partners" is not information. */
  readonly recipient: string;
  /** How long what it writes survives on the device. */
  readonly lifetime: string;
}

/**
 * Everything non-essential this application puts on a device.
 *
 * Empty, and that is a fact about the product rather than an omission. Adding
 * an entry here is what turns the banner on; nothing else has to change, and
 * nothing may load until `isAllowed` says so.
 */
export const TRACKERS: readonly TrackerDeclaration[] = [];

/** Categories that actually have something in them. */
export function declaredCategories(
  trackers: readonly TrackerDeclaration[] = TRACKERS,
): readonly Exclude<ConsentCategory, 'necessary'>[] {
  return [...new Set(trackers.map((tracker) => tracker.category))];
}

/** A banner is only lawful — and only honest — when there is something to consent to. */
export function consentIsRequired(trackers: readonly TrackerDeclaration[] = TRACKERS): boolean {
  return trackers.length > 0;
}

export interface ConsentRecord {
  /** Bumped when the declared purposes change, which re-asks rather than assumes. */
  readonly version: number;
  readonly decidedAt: string;
  readonly categories: Readonly<Partial<Record<Exclude<ConsentCategory, 'necessary'>, boolean>>>;
}

/**
 * Bump this when `TRACKERS` gains a purpose.
 *
 * Consent is given for stated purposes. Adding one and keeping the old record
 * would be treating a decision about yesterday's list as agreement to today's.
 */
export const CONSENT_VERSION = 1;

const STORAGE_KEY = 'gsi.consent';

/**
 * The bit of the storage API this module uses.
 *
 * Declared here rather than taken from the DOM lib on purpose. It keeps the
 * module free of a global at the type level, which is what lets it be tested
 * without a browser — and a consent module that can only be exercised in a
 * browser is one whose failure paths never get a test.
 */
export interface ConsentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The store, or nothing.
 *
 * `localStorage` is absent outside a browser and *throws on access* in a
 * private window with site data blocked — not on read, on the property lookup
 * itself. Both are ordinary situations, and neither may take the page down.
 */
function defaultStorage(): ConsentStorage | null {
  try {
    const candidate = (globalThis as { localStorage?: ConsentStorage }).localStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

/**
 * Reads the stored decision.
 *
 * Every access is wrapped: `localStorage` throws in a private window with site
 * data blocked, and a consent module that crashes the page when somebody has
 * locked their browser down is a consent module that punishes caution.
 *
 * An unreadable or outdated record is treated as no decision, which means
 * refusal. Failing towards "we may not" is the only safe direction here.
 */
export function readConsent(
  storage: ConsentStorage | null = defaultStorage(),
): ConsentRecord | null {
  if (storage === null) return null;

  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.decidedAt !== 'string') return null;
    if (typeof parsed.categories !== 'object') return null;
    return {
      version: CONSENT_VERSION,
      decidedAt: parsed.decidedAt,
      categories: parsed.categories,
    };
  } catch {
    return null;
  }
}

/**
 * Records a decision.
 *
 * Writing this is itself strictly necessary: without it the person is asked
 * again on every page, which the CNIL is explicit is not acceptable. It holds
 * the choice and its date, and nothing about who made it.
 */
export function writeConsent(
  categories: ConsentRecord['categories'],
  now: Date = new Date(),
  storage: ConsentStorage | null = defaultStorage(),
): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    decidedAt: now.toISOString(),
    categories,
  };
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // A refusal we cannot persist is still a refusal for this page. Asking
    // again next time is the correct failure; assuming agreement is not.
  }
  return record;
}

/** Forgets the decision, so the person is asked afresh. */
export function clearConsent(storage: ConsentStorage | null = defaultStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do, and nothing was granted by the failure.
  }
}

/**
 * The one question anything non-essential must ask before it loads.
 *
 * `necessary` is always true and takes no consent, by definition. Everything
 * else is false until explicitly granted — including when no decision exists,
 * which is the case the law cares most about.
 */
export function isAllowed(category: ConsentCategory, record: ConsentRecord | null): boolean {
  if (category === 'necessary') return true;
  return record?.categories[category] === true;
}

/** Every declared category set to false: the state before anyone chooses, and what "Refuser" writes. */
export function refuseAll(
  trackers: readonly TrackerDeclaration[] = TRACKERS,
): ConsentRecord['categories'] {
  return Object.fromEntries(declaredCategories(trackers).map((c) => [c, false]));
}

export function acceptAll(
  trackers: readonly TrackerDeclaration[] = TRACKERS,
): ConsentRecord['categories'] {
  return Object.fromEntries(declaredCategories(trackers).map((c) => [c, true]));
}
